import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaCalendarAlt } from "react-icons/fa";
import { useSelector } from "react-redux";
import styles from "../FeeType/styles.module.css";
import {
  CustomButton,
  CustomDropdown,
  CustomTextInput,
} from "../../../../components";
import AxiosInstance, {
  TokenService,
  sanitizeNameInput,
  hasInvalidNameChars,
} from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const CreateFeeDiscount = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);
  const editId = location.state?.ID || null;

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const user_id = user?.id || "";

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    // code: "",
    academicYear: "",
    type: "",
    amountPercentage: "",
    feeType: "", // ← new field for fee type dropdown
    description: "",
  });
  const [errors, setErrors] = useState({});

  // ── Academic Year dropdown options (from API) ──
  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [academicYearLoading, setAcademicYearLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  // ── Fee Type dropdown options (from API) ──
  const [feeTypeOptions, setFeeTypeOptions] = useState([]);
  const [feeTypeLoading, setFeeTypeLoading] = useState(false);
  const DELETE_DISCOUNT_URL = "schoolFees/delete/fees_discount";
  // type 0 = Amount (flat value), type 1 = Percentage
  const TYPE_OPTIONS = [
    { value: "0", label: translations.labels?.amount || "Amount" },
    { value: "1", label: translations.labels?.percentage || "Percentage" },
  ];

  // ── Fetch Fee Type list whenever the selected Academic Year changes ──
  useEffect(() => {
    fetchFeeTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school_id, formData.academicYear]);

  // ── Fetch Academic Year list on mount ──
  useEffect(() => {
    fetchAcademicYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school_id]);

  /** Academic Year list API: GET school/academic/{school_id} */
  const fetchAcademicYears = async () => {
    if (!school_id) return;
    setAcademicYearLoading(true);
    try {
      const response = await AxiosInstance.get(`/school/academic/${school_id}`);

      if (response?.data?.success && Array.isArray(response.data.data)) {
        const options = response.data.data
          .filter((item) => item.isActive === 1 || item.isUpcoming === 1)
          .map((item) => ({
            value: String(item.id),
            label: item.academic_year,
          }));
        setAcademicYearOptions(options);
      } else {
        setAcademicYearOptions([]);
      }
    } catch (error) {
      console.error("Error fetching academic years:", error);
      setAcademicYearOptions([]);
    } finally {
      setAcademicYearLoading(false);
    }
  };

  const fetchFeeTypes = async () => {
    if (!school_id || !formData.academicYear) return;
    setFeeTypeLoading(true);
    try {
      const response = await AxiosInstance.post("schoolFees/list/fees_type", {
        school_id: Number(school_id),
        academic_id: Number(formData.academicYear),
        page: "",
        limit: "",
        search: "",
      });

      if (response?.data?.status && Array.isArray(response.data.data)) {
        const options = response.data.data.map((item) => ({
          value: String(item.id),
          label: item.name,
        }));
        setFeeTypeOptions(options);
      } else {
        setFeeTypeOptions([]);
      }
    } catch (error) {
      console.error("Error fetching fee types:", error);
      setFeeTypeOptions([]);
    } finally {
      setFeeTypeLoading(false);
    }
  };

  useEffect(() => {
    if (editId) {
      fetchFeeDiscountDetails();
    }
  }, [editId]);

  /** View API: schoolFees/discountView  { id } */
  const fetchFeeDiscountDetails = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post("schoolFees/discountView", {
        id: editId,
      });

      if (response?.data?.status) {
        const data = Array.isArray(response.data.data)
          ? response.data.data[0]
          : response.data.data;

        const rawAcademicYear = data?.academic_year_id;

        setFormData({
          name: data?.name || "",
          // code: data?.code || "",
          academicYear:
            rawAcademicYear !== undefined && rawAcademicYear !== null
              ? rawAcademicYear.toString()
              : "",
          type:
            data?.type !== undefined && data?.type !== null
              ? data.type.toString()
              : "",
          amountPercentage:
            data?.value !== undefined && data?.value !== null
              ? data.value.toString()
              : "",
          feeType:
            data?.fees_type !== undefined && data?.fees_type !== null
              ? data.fees_type.toString()
              : "",
          description: data?.description || "",
        });
      }
    } catch (error) {
      console.error("Error fetching fee discount details:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.somethingWrong ||
          "Failed to fetch fee discount details. Please try again.",
        redirectPath: "",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  /* Name rejects special characters and emojis as they are typed/pasted. */
  const handleNameChange = (e) => {
    const clean = sanitizeNameInput(e.target.value);
    setFormData((prev) => ({ ...prev, name: clean }));
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: "" }));
    }
  };

  const handleAmountPercentageChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 7);
    setFormData((prev) => ({ ...prev, amountPercentage: digitsOnly }));
    if (errors.amountPercentage) {
      setErrors((prev) => ({ ...prev, amountPercentage: "" }));
    }
  };

  const handleDropdownChange = (name, value) => {
    // Normalize: CustomDropdown may hand back a raw value, an option object,
    // or a synthetic event. Always store the underlying value ("0" / "1").
    const finalValue =
      value && typeof value === "object"
        ? (value.value ?? value.target?.value ?? "")
        : value;

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = translations.labels?.nameRequired || "Name is required";
    } else if (hasInvalidNameChars(formData.name)) {
      // Safety net for values loaded from the API in edit mode
      newErrors.name =
        translations.labels?.specialCharactersNotAllowed ||
        "Special characters and emojis are not allowed";
    }
    // if (!formData.code?.trim()) {
    //   newErrors.code = translations.labels?.codeRequired || "Code is required";
    // }
    if (!formData.academicYear) {
      newErrors.academicYear =
        translations.labels?.academicYearRequired ||
        "Academic Year is required";
    }
    if (formData.type === "" || formData.type === null) {
      newErrors.type = translations.labels?.typeRequired || "Type is required";
    }
    if (!formData.amountPercentage?.toString().trim()) {
      newErrors.amountPercentage =
        translations.labels?.amountPercentageRequired ||
        "Amount/Percentage is required";
    }
    if (!formData.feeType) {
      newErrors.feeType =
        translations.labels?.feeTypeRequired || "Fee Type is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        id: editId || "",
        school_id: school_id,
        academic_year_id: Number(formData.academicYear),
        name: formData.name,
        // code: formData.code,
        type: Number(formData.type),
        value: Number(formData.amountPercentage),
        fees_type: Number(formData.feeType),
        description: formData.description,
        created_by: user_id,
      };

      const response = await AxiosInstance.post(
        "schoolFees/discountCreate",
        payload,
      );

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message: editId
            ? translations.labels?.feeDiscountUpdatedSuccess ||
              "Fee discount updated successfully!"
            : translations.labels?.feeDiscountCreatedSuccess ||
              "Fee discount created successfully!",
          redirectPath: "/user/listFeeDiscount",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            translations.labels?.somethingWrong ||
            "Something went wrong!",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error submitting fee discount:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          translations.labels?.somethingWrong ||
          "Failed to save fee discount. Please try again.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Delete handler.
   * NOTE: no delete endpoint was provided for fee discount, so this assumes the
   * same naming convention. Update the URL when you have the real one.
   */
  const handleDelete = async () => {
    try {
      const response = await AxiosInstance.put(DELETE_DISCOUNT_URL, {
        id: Number(editId),
        deleted_by: Number(user_id),
      });

      setDeleteModalOpen(false);

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            response?.data?.message || "Fee discount deleted successfully!",
          redirectPath: "/user/listFeeDiscount",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            "Failed to delete fee discount. Please try again.",
          redirectPath: "",
        });
      }
    } catch (err) {
      console.error("Error deleting fee discount:", err);
      setDeleteModalOpen(false);
      setModalState({
        open: true,
        type: "error",
        message:
          err?.response?.data?.message ||
          "Failed to delete fee discount. Please try again.",
        redirectPath: "",
      });
    }
  };

  const handleCancel = () => {
    navigate("/user/listFeeDiscount");
  };

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {editId
              ? translations.labels?.editFeeDiscount || "Edit Fee Discount"
              : translations.labels?.createFeeDiscount || "Create Fee Discount"}
          </h1>
        </div>
        <div className={styles.loadingContainer}>
          <p>{translations.labels?.loading || "Loading..."}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? translations.labels?.success || "Success"
            : translations.labels?.failed || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      <PopupModal
        open={deleteModalOpen}
        title={translations?.labels?.delete || "Confirm Delete"}
        message={
          translations?.labels?.confirmDeleteMessage ||
          "Are you sure you want to delete?"
        }
        onOk={handleDelete}
        onClose={() => setDeleteModalOpen(false)}
      />

      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {editId
              ? translations.labels?.editFeeDiscount || "Edit Fee Discount"
              : translations.labels?.createFeeDiscount || "Create Fee Discount"}
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={translations.labels?.name || "Name"}
                name="name"
                type="text"
                value={formData.name}
                onChange={handleNameChange}
                placeholder={translations.labels?.name || ""}
                required
                error={errors.name}
              />
            </div>

            <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.academicYear || "Academic Year"}
                name="academicYear"
                value={formData.academicYear}
                onChange={(value) =>
                  handleDropdownChange("academicYear", value)
                }
                options={academicYearOptions}
                placeholder={
                  academicYearLoading
                    ? translations.labels?.loading || "Loading..."
                    : translations.labels?.selectAcademicYear ||
                      "Select academic year"
                }
                required
                error={errors.academicYear}
                disabled={!!editId || academicYearLoading}
              />
            </div>

            {/* <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={`${translations.labels?.code} `}
                name="code"
                type="text"
                required
                value={formData.code}
                onChange={handleInputChange}
                placeholder={translations.labels?.code || ""}
                error={errors.code}

              />
            </div> */}

            <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.type || "Type"}
                name="type"
                value={formData.type}
                onChange={(value) => handleDropdownChange("type", value)}
                options={TYPE_OPTIONS}
                placeholder={translations.labels?.selectType || "Select type"}
                required
                error={errors.type}
              />
            </div>

            <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={
                  translations.labels?.amountPercentage || "Amount / Percentage"
                }
                name="amountPercentage"
                type="text"
                value={formData.amountPercentage}
                onChange={handleAmountPercentageChange}
                placeholder={translations.labels?.amountPercentage || ""}
                required
                error={errors.amountPercentage}
              />
            </div>

            {/* ── Fee Type dropdown (from API) ── */}
            <div className="col-lg-3 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={translations.labels?.feeType || "Fee Type"}
                name="feeType"
                value={formData.feeType}
                onChange={(value) => handleDropdownChange("feeType", value)}
                options={feeTypeOptions}
                placeholder={
                  feeTypeLoading
                    ? translations.labels?.loading || "Loading..."
                    : translations.labels?.selectFeeType || "Select Fee Type"
                }
                required
                error={errors.feeType}
                isDisabled={feeTypeLoading}
              />
            </div>

            <div className="col-lg-12 px-2 col-md-12 col-sm-12">
              <CustomTextInput
                label={`${translations.labels?.description} ${translations.labels?.optional}`}
                name="description"
                type="text"
                multiline
                rows={2}
                value={formData.description}
                onChange={handleInputChange}
                placeholder={translations.labels?.enterDescription || ""}
              />
            </div>
          </div>

          <div className={styles.buttonContainer}>
            {editId && (
              <CustomButton
                type="button"
                variant="contained"
                fullWidth={false}
                onClick={() => setDeleteModalOpen(true)}
                style={{
                  backgroundColor: "#dc3545",
                  borderColor: "#dc3545",
                  color: "#fff",
                }}
              >
                {translations?.labels?.deleteFeeDiscount || "Delete"}
              </CustomButton>
            )}

            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
            >
              {translations.labels?.cancel || "CANCEL"}
            </CustomButton>

            <CustomButton
              type="submit"
              variant="contained"
              className={styles.submitButton}
              fullWidth={false}
              loading={submitting}
              loadingText={
                editId
                  ? translations.labels?.updating || "Updating..."
                  : translations.labels?.submitting || "Submitting..."
              }
            >
              {editId
                ? translations.labels?.update || "UPDATE"
                : translations.labels?.submit || "SUBMIT"}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateFeeDiscount;
